/* eslint-disable max-len */
import { useEffect, useState } from "react";
import {
  SelectInput,
  SimpleForm,
  useCreate,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate
} from "react-admin";
import { ShowInDialogButton } from "@react-admin/ra-form-layout";
import { IconButton, TextField } from "@mui/material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { muiColors } from "themes/colors";
import { CustomButton } from "./CustomButton";
import CloseIcon from "@mui/icons-material/Close";
import * as Axios from "providers/axios";
import { LoadingIcon } from "assets/Icons";

const CustomToolbar = ({
  loading,
  amount
}: {
  loading: boolean;
  amount: number;
}) => {
  const translate = useTranslate();

  return (
    <div className="flex justify-between items-center px-[16px] pb-[16px]">
      <div>
        <div className="text-[12px] text-mono-secondaryTextColor font-['Inter'] font-[400]">
          {translate("order.final_amount")}:
        </div>
        <div className="text-[16px] text-mono-primaryTextColor font-['Inter'] font-[400]">
          ${amount}
        </div>
      </div>
      <CustomButton
        type="submit"
        variant="primary"
        cyId="credits-submit-button"
        text={translate("ra.action.save")}
        disabled={loading || !amount}
      />
    </div>
  );
};

export default function AddArbitaryCreditsModal({
  isOpen,
  setIsOpen,
  refundTransactionId,
  setRefundTransactionId
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => Promise<any>;
  txItemRefetch: () => Promise<any>;
  txAddonRefetch: () => Promise<any>;
  refundTransactionId?: string;
  setRefundTransactionId?: React.Dispatch<React.SetStateAction<string | null>>;
}) {
  const translate = useTranslate();
  const [create] = useCreate();
  const { api } = Axios.useAxios();
  const notify = useNotify();
  const [whatToAdd, setWhatToAdd] = useState<string | null>(
    refundTransactionId ? "STRIPE" : "STORE_CREDITS"
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState<number | string>(0);
  const [maxRefundAmount, setMaxRefundAmount] = useState<number | null>(null);
  const context = useRecordContext();
  const refresh = useRefresh();
  const isStripeRefund = Boolean(refundTransactionId && whatToAdd === "STRIPE");

  useEffect(() => {
    setWhatToAdd(refundTransactionId ? "STRIPE" : "STORE_CREDITS");
  }, [refundTransactionId]);

  useEffect(() => {
    if (!isStripeRefund || !refundTransactionId) {
      setMaxRefundAmount(null);
      return;
    }

    const getRefundAmount = async () => {
      setError(null);
      setLoading(true);

      try {
        const response = await api.post(
          "/v1/transaction-refund/calculate-refund-amount",
          { transactionId: refundTransactionId }
        );
        const maxAmount = Number(response.data?.maxRefundAmount || 0);
        setMaxRefundAmount(maxAmount);
        setAmount(maxAmount > 0 ? maxAmount : 0);
      } catch (e) {
        setError(
          e.response?.data?.message ||
            translate("order.refund_amount_load_error")
        );
      } finally {
        setLoading(false);
      }
    };

    getRefundAmount();
  }, [api, isStripeRefund, refundTransactionId, translate]);

  const resetStates = () => {
    setError(null);
    setAmount(0);
    setMaxRefundAmount(null);
    setLoading(false);
  };

  const closeModal = () => {
    setWhatToAdd(refundTransactionId ? "STRIPE" : "STORE_CREDITS");
    resetStates();
    setIsOpen(false);
    refresh();
    if (setRefundTransactionId) {
      setRefundTransactionId(null);
    }
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      const numericAmount = amount === "" ? 0 : Number(amount);

      if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
        setError(translate("order.amount_required"));
        return;
      }

      if (
        isStripeRefund &&
        maxRefundAmount !== null &&
        numericAmount > maxRefundAmount
      ) {
        setError(translate("order.refund_amount_exceeds_max"));
        return;
      }

      if (isStripeRefund && refundTransactionId) {
        await api.post("/v1/transaction-refund/refund", {
          amount: Number(numericAmount.toFixed(2)),
          transactionId: refundTransactionId
        });
        notify(translate("order.refund_success"), {
          type: "success",
          undoable: false
        });
        closeModal();
        return;
      }

      if (whatToAdd === "STORE_CREDITS") {
        create("Refund", {
          data: {
            amount: numericAmount,
            consumed: false,
            appointment: context.appointmentId,
            company: context.location.company_id,
            user: context.user_id
          }
        });

        closeModal();
      }
    } catch (e) {
      setError(
        e.response?.data?.message ||
          e.message ||
          translate("order.refund_submit_error")
      );
    } finally {
      setLoading(false);
      refresh();
    }
  };

  return (
    <>
      <ShowInDialogButton
        title={
          refundTransactionId
            ? translate("order.refund")
            : translate("order.add_arbitary_credits")
        }
        label=" "
        fullWidth
        ButtonProps={{
          value: translate("order.add_arbitary_credits"),
          variant: "contained",
          onClickCapture: () => setIsOpen(true),
          sx: { padding: 0, visibility: "hidden" }
        }}
        isOpen={isOpen}
        onClose={closeModal}
        close={closeModal}
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: muiColors.paper,
            borderRadius: "8px"
          },
          "& #edit-dialog-title": {
            textAlign: "center",
            textTransform: "uppercase",
            fontFamily: "Inter",
            fontSize: "14px"
          },
          "& .RaFormDialogTitle-closeButton": {
            display: "none"
          }
        }}
      >
        <>
          {loading && (
            <div className="w-full h-[100px] flex justify-center items-center">
              <div className="loadingSpinAnimation">
                <LoadingIcon />
              </div>
            </div>
          )}
          {!loading && (
            <SimpleForm
              onSubmit={onSubmit}
              resolver={yupResolver(validationSchema)}
              toolbar={
                <CustomToolbar
                  loading={loading}
                  amount={amount === "" ? 0 : Number(amount)}
                />
              }
              sx={{
                "& .MuiFilledInput-root": {
                  borderRadius: "8px",
                  border: "1px solid rgba(48, 47, 46, 0.06)"
                },
                "& .MuiFilledInput-input": {
                  height: "24px",
                  fontSize: "16px",
                  borderRadius: "8px",
                  fontFamily: "Inter",
                  border: "none"
                },
                "& .MuiFilledInput-input:focus": {
                  boxShadow: "none",
                  borderRadius: "8px !important"
                },
                "& .MuiFilledInput-root:before": {
                  border: "none !important"
                },
                "& .MuiFilledInput-root:after": {
                  border: "1px solid #b8722a !important",
                  top: "0px",
                  borderRadius: "8px",
                  transition: "none"
                },
                "& .MuiFilledInput-root.Mui-error:after": {
                  border: "1px solid #EF5B1B !important"
                },
                "& .MuiFilledInput-root.Mui-disabled": {
                  backgroundColor: "rgba(48, 47, 46, 0.06)"
                },
                "& .MuiTextField-root": {
                  marginTop: "0px",
                  marginBottom: "0px"
                },
                "& .MuiInputLabel-root": {
                  fontSize: "16px",
                  color: "rgba(48, 47, 46, 0.6)"
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#b8722a"
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "12px",
                  marginTop: "2px",
                  marginBottom: "2px",
                  lineHeight: "14px"
                },
                "[type='number']:focus": {
                  boxShadow: "none"
                }
              }}
            >
              {error && <p style={{ color: "red" }}>{error}</p>}
              <SelectInput
                source={translate("order.where_to_add")}
                data-cy="where-to-add-select"
                choices={[
                  { id: "STORE_CREDITS", name: "Store Credits" },
                  { id: "STRIPE", name: "Original Payment Method" }
                ]}
                fullWidth
                onChange={(e) => {
                  resetStates();
                  setWhatToAdd(e.target.value);
                }}
                defaultValue={whatToAdd || ""}
                disabled={!refundTransactionId}
                sx={{
                  "& .MuiFilledInput-root, .MuiFilledInput-input, .MuiFilledInput-input:focus, .MuiFilledInput-root:after":
                    {
                      borderBottomLeftRadius:
                        whatToAdd === "FREEFORM" || whatToAdd === "DISCOUNT"
                          ? "0px"
                          : "8px",
                      borderBottomRightRadius:
                        whatToAdd === "FREEFORM" || whatToAdd === "DISCOUNT"
                          ? "0px"
                          : "8px"
                    },
                  "& .MuiFormHelperText-root": {
                    display: "none"
                  }
                }}
              />
              {isStripeRefund && maxRefundAmount !== null && (
                <div className="text-[12px] text-mono-secondaryTextColor font-['Inter'] mb-2">
                  {translate("order.maximum_refundable")}: $
                  {maxRefundAmount.toFixed(2)}
                </div>
              )}
              <TextField
                variant="filled"
                type="number"
                label={translate("order.amount")}
                InputProps={{
                  inputProps: {
                    max: isStripeRefund
                      ? maxRefundAmount || undefined
                      : undefined,
                    min: 0,
                    nullable: true,
                    step: "0.01",
                    "data-cy": "amount-input"
                  }
                }}
                value={amount.toString()}
                onFocus={(e) => {
                  if (e.target.value === "0") {
                    setAmount("");
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === "") {
                    setAmount(0);
                  }
                }}
                onChange={(e) => {
                  const value = +e.target.value;
                  if (value < 0) {
                    setAmount(0);
                  } else if (
                    isStripeRefund &&
                    maxRefundAmount !== null &&
                    value > maxRefundAmount
                  ) {
                    setAmount(maxRefundAmount);
                  } else {
                    if (e.target.value === "") {
                      setAmount("");
                    } else {
                      setAmount(value);
                    }
                  }
                }}
                fullWidth
                sx={{
                  marginTop: "16px !important",
                  "& .MuiFilledInput-input": {
                    paddingTop: "22px",
                    paddingBottom: "5px",
                    fontWeight: 400
                  },
                  "& .MuiFormHelperText-root": {
                    display: "none"
                  }
                }}
              />
              <IconButton
                sx={{
                  position: "absolute",
                  top: "8px",
                  right: "8px"
                }}
                onClick={closeModal}
              >
                <CloseIcon />
              </IconButton>
            </SimpleForm>
          )}
        </>
      </ShowInDialogButton>
    </>
  );
}

const validationSchema = yup.object().shape({});
